/**
 * ProvisionWizardDrawer — Multi-step drawer for creating + provisioning a new tenant.
 *
 * Original wizard steps (0–3): Info → Plan → Admin User → Review
 * Extended configuration tabs (4–8): Basic Info → Transfer Options → Client Details
 *   → Company Details → AWS S3 Configuration
 *
 * Renders as a right-side slide-out panel. The original step-based flow is
 * preserved; new configuration tabs are appended after the review step.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { Steps } from 'primereact/steps'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { InputSwitch } from 'primereact/inputswitch'
import { tenantSlugSchema } from '@kc3/validation'
import { DEFAULT_TENANT_RECORDING_CONFIG } from '@kc3/contracts'

import type { Tenant } from '../../hooks/useTenants.js'
import { usePlanLookup } from '../../hooks/usePlans.js'
import { useTimezones } from '../../hooks/useTimezones.js'

import {
  ProvisionStepReview,
  type ProvisionWizardData,
  type ProvisionStepReviewRef,
} from './wizard/ProvisionStepReview.js'

import {
  ProvisionStepRecording,
  validateRecording,
} from './wizard/ProvisionStepRecording.js'

import { getSeedPreviewData } from './seedPreviewData.js'

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

export interface ProvisionWizardDrawerProps {
  open: boolean
  onClose: () => void
  mode?: 'create' | 'view' | 'edit'
  tenant?: Tenant | undefined
}

const WIZARD_STEPS = [
  { label: 'Basic Info' },
  { label: 'Transfer' },
  { label: 'Client & Company' },
  { label: 'Recording' },
  { label: 'AWS S3' },
  { label: 'Transcription' },
  { label: 'Defaults' },
  { label: 'Review' },
] as const

const REVIEW_STEP = WIZARD_STEPS.length - 1

/** Live assist LLM presets for the self-hosted engine (Ollama tags). */
const DEFAULT_SELF_HOSTED_MODEL = 'ollama:gemma4:e4b'
const SELF_HOSTED_MODELS = [
  { label: 'Gemma 4 E4B — low latency (recommended)', value: 'ollama:gemma4:e4b' },
  { label: 'Gemma 4 12B — stronger reasoning, slower', value: 'ollama:gemma4:12b' },
  { label: 'Gemma 4 E2B — smallest, CPU-friendly', value: 'ollama:gemma4:e2b' },
]
/** The compose `stt-whisper` service as seen from api / ws-gateway. */
const DEFAULT_WHISPER_URL = 'http://stt-whisper:8178'
const WHISPER_URL_RE = /^(https?|wss?):\/\/[^\s/]+(\/\S*)?$/

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'ca-central-1',
  'eu-west-1', 'eu-west-2', 'eu-central-1', 'ap-south-1', 'ap-southeast-1',
  'ap-southeast-2', 'ap-northeast-1', 'me-central-1',
]

/** Mirrors IAM_ARN_RE in apps/api/src/routes/tenants/update.ts. */
const IAM_ARN_RE = /^arn:aws[a-z-]*:iam::\d{12}:(user|role)\/[A-Za-z0-9+=,.@_/-]{1,512}$/

const TRANSCRIBE_LANGUAGES = [
  { label: 'English (US)', value: 'en-US' },
  { label: 'English (UK)', value: 'en-GB' },
  { label: 'English (India)', value: 'en-IN' },
  { label: 'English (Australia)', value: 'en-AU' },
  { label: 'Hindi (India)', value: 'hi-IN' },
  { label: 'Spanish (US)', value: 'es-US' },
  { label: 'Spanish (Spain)', value: 'es-ES' },
  { label: 'French (France)', value: 'fr-FR' },
  { label: 'German', value: 'de-DE' },
  { label: 'Arabic (Gulf)', value: 'ar-AE' },
]

const EMPTY_WIZARD_DATA: ProvisionWizardData = {
  slug: '',
  name: '',
  planId: null,
  status: 'active',
  domainUrl: '',
  timeZone: '',
  userLimitation: '100',
  clientFullName: '',
  clientEmail: '',
  clientMobileNumber: '',
  clientLandline: '',
  clientAddress: '',
  companyName: '',
  companyEmail: '',
  enableS3: false,
  awss3BucketName: '',
  awss3AccessKey: '',
  awss3Secret: '',
  awss3Region: '',
  awss3UrlExpiry: 60,
  postCallAnalytics: false,
  awsTranscribeAccessKey: '',
  awsTranscribeSecret: '',
  awsTranscribeRegion: '',
  awsTranscribeS3Bucket: '',
  awsTranscribeLanguageCode: 'en-US',
  awsTranscribeIamArn: '',
  manualCallHistoryUpload: false,
  showAgentSentiment: true,
  liveAssistEngine: 'aws',
  liveAssistWhisperUrl: '',
  liveAssistWhisperKey: '',
  liveAssistModel: DEFAULT_SELF_HOSTED_MODEL,
  companyType: 'general',
  seedDefaultData: false,
  recording: DEFAULT_TENANT_RECORDING_CONFIG,
  featureFlags: {},
}

interface TransferFeatureState {
  phoneService: boolean
  users: boolean
  ivr: boolean
  csatIvr: boolean
  callCenter: boolean
  queue: boolean
  ringGroup: boolean
  playGo: boolean
}

const DEFAULT_TRANSFER_FEATURES: TransferFeatureState = {
  phoneService: true,
  users: true,
  ivr: true,
  csatIvr: true,
  callCenter: true,
  queue: true,
  ringGroup: true,
  playGo: true,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProvisionWizardDrawer({
  open,
  onClose,
  mode = 'create',
  tenant,
}: ProvisionWizardDrawerProps): JSX.Element {
  const previousFocus = useRef<HTMLElement | null>(null)

  // Wizard state
  const [activeStep, setActiveStep] = useState(0)
  const [wizardData, setWizardData] = useState<ProvisionWizardData>(EMPTY_WIZARD_DATA)

  // Feature settings (stored in featureFlags JSONB)
  const [callAnalytics, setCallAnalytics] = useState(false)
  const [googleSignIn, setGoogleSignIn] = useState(false)
  const [googleClientId, setGoogleClientId] = useState('')
  const [googleClientSecret, setGoogleClientSecret] = useState('')
  const [microsoftSignIn, setMicrosoftSignIn] = useState(false)
  const [microsoftClientId, setMicrosoftClientId] = useState('')
  const [microsoftClientSecret, setMicrosoftClientSecret] = useState('')
  const [continueRecording, setContinueRecording] = useState(false)

  const [transferFeatures, setTransferFeatures] = useState<TransferFeatureState>(
    DEFAULT_TRANSFER_FEATURES,
  )

  const reviewRef = useRef<ProvisionStepReviewRef>(null)

  // Fetch plans for dropdown
  const { items: plans, loading: plansLoading } = usePlanLookup()
  const { items: timezones, loading: timezonesLoading } = useTimezones()

  // ---- Focus management ---------------------------------------------------

  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  // Populate wizard data when opening in view/edit mode
  useEffect(() => {
    if (open && (mode === 'view' || mode === 'edit') && tenant) {
      setWizardData({
        slug: tenant.slug,
        name: tenant.name,
        planId: tenant.planId,
        status: tenant.status as 'active' | 'suspended',
        domainUrl: tenant.domainUrl ?? '',
        timeZone: tenant.timeZone ?? '',
        userLimitation: tenant.userLimitation ?? '100',
        clientFullName: tenant.clientFullName ?? '',
        clientEmail: tenant.clientEmail ?? '',
        clientMobileNumber: tenant.clientMobileNumber ?? '',
        clientLandline: tenant.clientLandline ?? '',
        clientAddress: tenant.clientAddress ?? '',
        companyName: tenant.companyName ?? '',
        companyEmail: tenant.companyEmail ?? '',
        companyType: tenant.companyType ?? 'general',
        seedDefaultData: tenant.seedDefaultData ?? false,
        enableS3: tenant.enableS3 ?? false,
        awss3BucketName: tenant.awss3BucketName ?? '',
        awss3AccessKey: tenant.awss3AccessKey ?? '',
        awss3Secret: tenant.awss3Secret ?? '',
        awss3Region: tenant.awss3Region ?? '',
        awss3UrlExpiry: tenant.awss3UrlExpiry ?? 60,
        postCallAnalytics: tenant.postCallAnalytics ?? false,
        awsTranscribeAccessKey: tenant.awsTranscribeAccessKey ?? '',
        awsTranscribeSecret: '',
        awsTranscribeRegion: tenant.awsTranscribeRegion ?? '',
        awsTranscribeS3Bucket: tenant.awsTranscribeS3Bucket ?? '',
        awsTranscribeLanguageCode: tenant.awsTranscribeLanguageCode ?? 'en-US',
        awsTranscribeIamArn: tenant.awsTranscribeIamArn ?? '',
        manualCallHistoryUpload: tenant.manualCallHistoryUpload ?? false,
        showAgentSentiment: tenant.showAgentSentiment ?? true,
        liveAssistEngine: tenant.liveAssistEngine ?? 'aws',
        liveAssistWhisperUrl: tenant.liveAssistWhisperUrl ?? '',
        liveAssistWhisperKey: '',
        liveAssistModel: tenant.liveAssistModel ?? DEFAULT_SELF_HOSTED_MODEL,
        recording: tenant.recordingConfig
          ? {
              recordingEnabled: tenant.recordingConfig.recordingEnabled,
              announcementEnabled: tenant.recordingConfig.announcementEnabled,
              announcementMessage: tenant.recordingConfig.announcementMessage,
              recordingType: tenant.recordingConfig.recordingType,
              retentionDays: tenant.recordingConfig.retentionDays,
              allowAgentPause: tenant.recordingConfig.allowAgentPause ?? false,
            }
          : DEFAULT_TENANT_RECORDING_CONFIG,
        featureFlags: (tenant.featureFlags as Record<string, boolean>) ?? {},
      })
      // Populate feature checkboxes from featureFlags
      const flags = tenant.featureFlags as Record<string, any> | null
      setCallAnalytics(flags?.callAnalytics ?? false)
      const googleConfig = flags?.googleSignIn as { enabled?: boolean; clientId?: string } | undefined
      setGoogleSignIn(googleConfig?.enabled ?? false)
      setGoogleClientId(googleConfig?.clientId ?? '')
      setGoogleClientSecret('')
      const microsoftConfig = flags?.microsoftSignIn as { enabled?: boolean; clientId?: string } | undefined
      setMicrosoftSignIn(microsoftConfig?.enabled ?? false)
      setMicrosoftClientId(microsoftConfig?.clientId ?? '')
      setMicrosoftClientSecret('')
      setContinueRecording(flags?.continueRecording ?? false)
      setTransferFeatures({
        phoneService: flags?.phoneService ?? true,
        users: flags?.users ?? true,
        ivr: flags?.ivr ?? true,
        csatIvr: flags?.csatIvr ?? true,
        callCenter: flags?.callCenter ?? true,
        queue: flags?.queue ?? true,
        ringGroup: flags?.ringGroup ?? true,
        playGo: flags?.playGo ?? true,
      })
    } else if (open && mode === 'create') {
      setWizardData(EMPTY_WIZARD_DATA)
    }
  }, [open, mode, tenant])

  // Reset everything when the drawer closes so re-opening starts fresh.
  useEffect(() => {
    if (!open) {
      setActiveStep(0)
      setWizardData(EMPTY_WIZARD_DATA)
      setCallAnalytics(false)
      setGoogleSignIn(false)
      setGoogleClientId('')
      setGoogleClientSecret('')
      setMicrosoftSignIn(false)
      setMicrosoftClientId('')
      setMicrosoftClientSecret('')
      setContinueRecording(false)
      setTransferFeatures(DEFAULT_TRANSFER_FEATURES)
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // ---- Callbacks ----------------------------------------------------------

  const handleComplete = (_tenant: Tenant): void => {
    void _tenant
    onClose()
  }

  // ---- Render helpers for extended tabs -----------------------------------

  const featureCheckbox = (
    checked: boolean,
    onChange: (val: boolean) => void,
    title: string,
    desc: string,
  ): JSX.Element => (
    <label className="flex items-start gap-3 p-4 border rounded-sm border-gray-100 hover:border-[#3454cf]/20 transition-all bg-white cursor-pointer group">
      <div className="mt-0.5">
        <Checkbox checked={checked} onChange={(e) => onChange(e.checked ?? false)} disabled={mode === 'view'} />
      </div>
      <div>
        <div className="font-semibold text-gray-800 text-sm group-hover:text-[#3454cf] transition-colors">{title}</div>
        <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
      </div>
    </label>
  )

  const transferBox = (
    key: keyof TransferFeatureState,
    title: string,
    desc: string,
  ): JSX.Element => (
    <label className="flex items-start justify-between p-4 border rounded-sm border-gray-100 bg-white hover:border-[#3454cf]/20 transition-all cursor-pointer group">
      <div>
        <div className="font-semibold text-gray-800 text-sm group-hover:text-[#3454cf] transition-colors">{title}</div>
        <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
      </div>
      <div className="mt-0.5 ml-3">
        <Checkbox
          checked={transferFeatures[key]}
          onChange={(e) =>
            setTransferFeatures((prev) => ({ ...prev, [key]: e.checked ?? false }))
          }
          disabled={mode === 'view'}
        />
      </div>
    </label>
  )

  // ---- Step content renderers ---------------------------------------

  const renderCombinedBasicInfoTab = (): JSX.Element => {
    const slugValid = tenantSlugSchema.safeParse(wizardData.slug).success
    const slugShowError = wizardData.slug.length > 0 && !slugValid
    
    const isValidUrl = (url: string): boolean => {
      if (!url) return true
      const trimmedUrl = url.trim()
      if (!trimmedUrl) return false
      try {
        const urlObj = new URL(trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`)
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
      } catch {
        return false
      }
    }
    
    const domainUrlValid = !wizardData.domainUrl || isValidUrl(wizardData.domainUrl)
    const domainUrlShowError = wizardData.domainUrl && !domainUrlValid

    const selectedPlan = plans.find((p) => p.id === wizardData.planId)

    const handlePlanChange = (planId: string | null): void => {
      setWizardData((prev) => {
        const updated = { ...prev, planId }
        if (planId) {
          const plan = plans.find((p) => p.id === planId)
          if (plan) {
            updated.userLimitation = plan.maxAgents.toString()
          }
        } else {
          updated.userLimitation = '100'
        }
        return updated
      })
    }

    return (
      <div className="flex flex-col gap-6">
        {/* Tenant Identity Section */}
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Tenant Identity</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="col-span-1 md:col-span-2 flex flex-col gap-1.5">
              <div className={`flex items-center justify-between p-5 border rounded-sm transition-all ${wizardData.status === 'active' ? 'bg-emerald-50/30 border-emerald-100' : 'bg-gray-50/50 border-gray-100'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${wizardData.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                    <i className={wizardData.status === 'active' ? 'pi pi-check text-sm' : 'pi pi-times text-sm'} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">
                      {wizardData.status === 'active' ? 'Active Status' : 'Suspended Status'}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {wizardData.status === 'active' ? 'Tenant is active' : 'Tenant is suspended'}
                    </div>
                  </div>
                </div>
                <InputSwitch
                  checked={wizardData.status === 'active'}
                  onChange={(e) => setWizardData((prev) => ({ ...prev, status: e.value ? 'active' : 'suspended' }))}
                  disabled={mode === 'view'}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Tenant slug <span className="text-red-500">*</span>
              </label>
              <InputText
                value={wizardData.slug}
                onChange={(e) => setWizardData((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="acme_corp"
                className="w-full"
                aria-invalid={slugShowError ? 'true' : 'false'}
                disabled={mode === 'view'}
              />
              {slugShowError ? (
                <small className="text-red-600">Invalid slug</small>
              ) : (
                <small className="text-gray-500">lowercase, 2-40 chars; letters, digits, and underscores only</small>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Display name <span className="text-red-500">*</span>
              </label>
              <InputText
                value={wizardData.name}
                onChange={(e) => setWizardData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Acme Corp"
                className="w-full"
                disabled={mode === 'view'}
              />
              <small className="text-gray-500">Shown in the admin UI. 1-100 characters.</small>
            </div>
          </div>
        </div>

        {/* Basic Configuration Section */}
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Basic Configuration</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Domain URL <span className="text-red-500">*</span>
              </label>
              <InputText
                value={wizardData.domainUrl}
                onChange={(e) => setWizardData((prev) => ({ ...prev, domainUrl: e.target.value }))}
                onBlur={(e) => setWizardData((prev) => ({ ...prev, domainUrl: e.target.value.trim() }))}
                placeholder="https://example.com"
                className={`w-full ${domainUrlShowError ? 'p-invalid' : ''}`}
                aria-invalid={domainUrlShowError ? 'true' : 'false'}
                disabled={mode === 'view'}
              />
              {domainUrlShowError ? (
                <small className="text-red-600">Invalid URL format. Please enter a valid URL (e.g., https://example.com)</small>
              ) : (
                <small className="text-gray-500">Enter the domain URL (e.g., https://example.com)</small>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Plan <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={wizardData.planId}
                options={plans.map((p) => ({ label: p.name, value: p.id }))}
                onChange={(e) => handlePlanChange(e.value as string | null)}
                placeholder="Select Plan"
                className="w-full"
                loading={plansLoading}
                disabled={mode === 'view'}
                filter
                appendTo={document.body}
                pt={{ panel: { style: { zIndex: 100000 } }, list: { className: 'max-h-60 overflow-auto' } } as any}
              />
              <small className="text-gray-500">Select a plan to set user limits</small>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Time Zone <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={wizardData.timeZone}
                options={timezones.map((tz) => ({ label: tz.label, value: tz.value }))}
                onChange={(e) => setWizardData((prev) => ({ ...prev, timeZone: e.value as string }))}
                placeholder="Select Time Zone"
                className="w-full"
                loading={timezonesLoading}
                disabled={mode === 'view'}
                filter
                appendTo={document.body}
                pt={{ panel: { style: { zIndex: 100000 } }, list: { className: 'max-h-60 overflow-auto' } } as any}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                User Limitation <span className="text-red-500">*</span>
              </label>
              <InputText
                value={wizardData.userLimitation}
                onChange={(e) => setWizardData((prev) => ({ ...prev, userLimitation: e.target.value }))}
                placeholder="100"
                className="w-full"
                disabled={mode === 'view' || !selectedPlan}
              />
              {!selectedPlan ? (
                <small className="text-gray-500">Select a plan to auto-populate user limit</small>
              ) : (
                <small className="text-gray-500">Auto-populated from plan (maxAgents: {selectedPlan.maxAgents})</small>
              )}
            </div>
          </div>
        </div>

        {/* Feature Settings */}
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Feature Settings</h3>
          </div>
          <div className="p-6 flex flex-col gap-3">
            {featureCheckbox(callAnalytics, setCallAnalytics, 'Call Analytics', 'Enable call analytics and reporting')}
            {featureCheckbox(googleSignIn, setGoogleSignIn, 'Google Sign-In Integration', 'Allow users to sign in with Google')}
            {featureCheckbox(microsoftSignIn, setMicrosoftSignIn, 'Microsoft Sign-In Integration', 'Allow users to sign in with Microsoft Azure AD / Entra')}
            
            {/* Google Sign-In Configuration */}
            {googleSignIn && (
              <div className="ml-8 mt-2 p-4 bg-blue-50 border border-blue-100 rounded-sm space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Google Client ID <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    value={googleClientId}
                    onChange={(e) => setGoogleClientId(e.target.value)}
                    placeholder="xxx.apps.googleusercontent.com"
                    className="w-full text-sm"
                    disabled={mode === 'view'}
                  />
                  <small className="text-gray-600 text-xs mt-1 block">
                    Get this from Google Cloud Console → Credentials
                  </small>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Google Client Secret {mode === 'edit' ? '' : <span className="text-red-500">*</span>}
                  </label>
                  <InputText
                    type="password"
                    value={googleClientSecret}
                    onChange={(e) => setGoogleClientSecret(e.target.value)}
                    placeholder="GOCSPX-xxxxxxxxxxxxx"
                    className="w-full text-sm"
                    disabled={mode === 'view'}
                  />
                  <small className="text-gray-600 text-xs mt-1 block">
                    {mode === 'edit'
                      ? 'Leave blank to keep the existing secret. Enter a value only to update it.'
                      : 'Keep this secret secure. Stored encrypted.'}
                  </small>
                </div>
              </div>
            )}
            
            {microsoftSignIn && (
              <div className="ml-8 mt-2 p-4 bg-blue-50 border border-blue-100 rounded-sm space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Microsoft Client ID <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    value={microsoftClientId}
                    onChange={(e) => setMicrosoftClientId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full text-sm"
                    disabled={mode === 'view'}
                  />
                  <small className="text-gray-600 text-xs mt-1 block">
                    Get this from Azure AD → App registrations
                  </small>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Microsoft Client Secret {mode === 'edit' ? '' : <span className="text-red-500">*</span>}
                  </label>
                  <InputText
                    type="password"
                    value={microsoftClientSecret}
                    onChange={(e) => setMicrosoftClientSecret(e.target.value)}
                    placeholder="tenant-secret-value"
                    className="w-full text-sm"
                    disabled={mode === 'view'}
                  />
                  <small className="text-gray-600 text-xs mt-1 block">
                    {mode === 'edit'
                      ? 'Leave blank to keep the existing secret. Enter a value only to update it.'
                      : 'Keep this secret secure. Stored encrypted.'}
                  </small>
                </div>
              </div>
            )}

            {featureCheckbox(continueRecording, setContinueRecording, 'Continue Recording On-Call Transfer', 'Keep recording active during call transfers')}
          </div>
        </div>

      </div>
    )
  }

  const renderTransferOptionsTab = (): JSX.Element => (
    <div>
      {continueRecording ? (
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Transfer Recording Features</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {transferBox('phoneService', 'Phone Service', 'Voice calling capabilities')}
            {transferBox('users', 'Users', 'User management system')}
            {transferBox('ivr', 'IVR', 'Interactive voice response')}
            {transferBox('csatIvr', 'CSAT IVR', 'Customer satisfaction surveys')}
            {transferBox('callCenter', 'Call Center', 'Advanced call center features')}
            {transferBox('queue', 'Queue', 'Call queue management')}
            {transferBox('ringGroup', 'Ring Group', 'Group calling functionality')}
            {transferBox('playGo', 'Play & Go', 'Quick call routing system')}
          </div>
        </div>
      ) : (
        <div className="border border-gray-100 border-dashed rounded-sm p-12 flex flex-col items-center justify-center text-center bg-gray-50/30">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <i className="pi pi-info-circle text-gray-400 text-sm" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-1">Transfer Recording Disabled</h3>
          <p className="text-sm text-gray-400 max-w-sm leading-relaxed">
            Enable &quot;Continue Recording On-Call Transfer&quot; in the Basic Info tab to configure these advanced transfer options.
          </p>
        </div>
      )}
    </div>
  )

  const renderCombinedClientCompanyTab = (): JSX.Element => (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Client Information</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Full Name</label>
            <InputText value={wizardData.clientFullName} onChange={(e) => setWizardData((prev) => ({ ...prev, clientFullName: e.target.value }))} className="w-full" disabled={mode === 'view'} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Client Email</label>
            <InputText value={wizardData.clientEmail} onChange={(e) => setWizardData((prev) => ({ ...prev, clientEmail: e.target.value }))} className="w-full" disabled={mode === 'view'} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Mobile Number</label>
            <InputText
              value={wizardData.clientMobileNumber}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, '').slice(0, 15)
                setWizardData((prev) => ({ ...prev, clientMobileNumber: numericValue }))
              }}
              className="w-full"
              keyfilter="num"
              maxLength={15}
              disabled={mode === 'view'}
            />
            <small className="text-gray-400 text-xs">Numbers only, max 15 digits</small>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Landline</label>
            <InputText
              value={wizardData.clientLandline}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, '').slice(0, 15)
                setWizardData((prev) => ({ ...prev, clientLandline: numericValue }))
              }}
              className="w-full"
              keyfilter="num"
              maxLength={15}
              disabled={mode === 'view'}
            />
            <small className="text-gray-400 text-xs">Numbers only, max 15 digits</small>
          </div>
          <div className="col-span-1 md:col-span-2 flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Address</label>
            <InputText value={wizardData.clientAddress} onChange={(e) => setWizardData((prev) => ({ ...prev, clientAddress: e.target.value }))} className="w-full" disabled={mode === 'view'} />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Company Information</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Company Name</label>
            <InputText value={wizardData.companyName} onChange={(e) => setWizardData((prev) => ({ ...prev, companyName: e.target.value }))} className="w-full" disabled={mode === 'view'} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Company Email</label>
            <InputText value={wizardData.companyEmail} onChange={(e) => setWizardData((prev) => ({ ...prev, companyEmail: e.target.value }))} className="w-full" disabled={mode === 'view'} />
          </div>
        </div>
      </div>
    </div>
  )

  const renderAwsS3Tab = (): JSX.Element => {
    const enableS3Value = wizardData.enableS3 ?? false
    return (
      <div className="space-y-5">
        <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#f6931c]/10 flex items-center justify-center">
              <i className="pi pi-cloud text-[#f6931c] text-sm" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Cloud Storage</p>
              <p className="text-xs text-gray-400">AWS S3 configuration</p>
            </div>
          </div>
          <label className="font-semibold text-sm text-gray-700 flex items-center gap-2 cursor-pointer">
            <Checkbox checked={enableS3Value} onChange={(e) => setWizardData((prev) => ({ ...prev, enableS3: e.checked ?? false }))} disabled={mode === 'view'} />
            Enable
          </label>
        </div>
        {enableS3Value && (
          <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Bucket Name <span className="text-red-500">*</span></label>
                <InputText value={wizardData.awss3BucketName ?? ''} onChange={(e) => setWizardData((prev) => ({ ...prev, awss3BucketName: e.target.value }))} className="w-full" disabled={mode === 'view'} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Access Key <span className="text-red-500">*</span></label>
                <InputText value={wizardData.awss3AccessKey ?? ''} onChange={(e) => setWizardData((prev) => ({ ...prev, awss3AccessKey: e.target.value }))} className="w-full" disabled={mode === 'view'} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Secret Key <span className="text-red-500">*</span></label>
                <InputText value={wizardData.awss3Secret ?? ''} onChange={(e) => setWizardData((prev) => ({ ...prev, awss3Secret: e.target.value }))} className="w-full" disabled={mode === 'view'} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">AWS Region <span className="text-red-500">*</span></label>
                <Dropdown
                  value={wizardData.awss3Region ?? ''}
                  options={[
                    { label: 'Select AWS Region', value: '' },
                    { label: 'us-east-1', value: 'us-east-1' },
                    { label: 'us-east-2', value: 'us-east-2' },
                    { label: 'us-west-1', value: 'us-west-1' },
                    { label: 'us-west-2', value: 'us-west-2' },
                    { label: 'eu-west-1', value: 'eu-west-1' },
                    { label: 'eu-central-1', value: 'eu-central-1' },
                    { label: 'ap-south-1', value: 'ap-south-1' },
                    { label: 'ap-southeast-1', value: 'ap-southeast-1' },
                    { label: 'ap-southeast-2', value: 'ap-southeast-2' },
                    { label: 'ap-northeast-1', value: 'ap-northeast-1' },
                  ]}
                  onChange={(e) => setWizardData((prev) => ({ ...prev, awss3Region: e.value }))}
                  className="w-full"
                  disabled={mode === 'view'}
                  filter
                  appendTo={document.body}
                  pt={{
                    panel: { style: { zIndex: 100000 } },
                    list: { className: 'max-h-60 overflow-auto' }
                  } as any}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">URL Expiry (seconds) <span className="text-red-500">*</span></label>
                <InputText
                  value={(wizardData.awss3UrlExpiry ?? '').toString()}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, '')
                    setWizardData((prev) => ({ ...prev, awss3UrlExpiry: numericValue ? parseInt(numericValue, 10) : 60 }))
                  }}
                  className="w-full"
                  keyfilter="num"
                  disabled={mode === 'view'}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderTranscriptionTab = (): JSX.Element => {
    const enabled = wizardData.postCallAnalytics ?? false
    const disabled = mode === 'view'
    const secretStored = mode !== 'create' && Boolean(tenant?.awsTranscribeSecretConfigured)
    const set = (patch: Partial<ProvisionWizardData>): void =>
      setWizardData((prev) => ({ ...prev, ...patch }))
    const arn = wizardData.awsTranscribeIamArn?.trim() ?? ''
    const arnInvalid = arn !== '' && !IAM_ARN_RE.test(arn)
    const arnIsRole = !arnInvalid && /:role\//.test(arn)
    const missing = enabled && (
      !wizardData.awsTranscribeAccessKey?.trim() ||
      !(secretStored || wizardData.awsTranscribeSecret?.trim()) ||
      !wizardData.awsTranscribeRegion ||
      !wizardData.awsTranscribeS3Bucket?.trim()
    )
    return (
      <div className="space-y-5">
        <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf]/10 flex items-center justify-center">
              <i className="pi pi-microphone text-[#3454cf] text-sm" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Post Call Analytics</p>
              <p className="text-xs text-gray-400">
                After every recorded call ends, the recording is sent to AWS Transcribe and the
                transcript is shown in Call History.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-gray-600">{enabled ? 'Enabled' : 'Disabled'}</span>
            <InputSwitch
              checked={enabled}
              onChange={(e) => set({ postCallAnalytics: e.value ?? false })}
              disabled={disabled}
              aria-label="Post call analytics"
            />
          </div>
        </div>

        <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf]/10 flex items-center justify-center">
              <i className="pi pi-upload text-[#3454cf] text-sm" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Manual Call History Upload</p>
              <p className="text-xs text-gray-400">
                Lets agents upload recordings captured outside the platform into Call History.
                Uploaded calls are encrypted, transcribed and QM-scored like live calls.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-gray-600">{wizardData.manualCallHistoryUpload ? 'Enabled' : 'Disabled'}</span>
            <InputSwitch
              checked={wizardData.manualCallHistoryUpload ?? false}
              onChange={(e) => set({ manualCallHistoryUpload: e.value ?? false })}
              disabled={disabled}
              aria-label="Manual call history upload"
            />
          </div>
        </div>

        <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf]/10 flex items-center justify-center">
              <i className="pi pi-eye text-[#3454cf] text-sm" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Show Agent Sentiment</p>
              <p className="text-xs text-gray-400">
                Shows the agent-side sentiment from call analytics on Quality Management dashboards.
                Turn off to hide it (customer sentiment is unaffected; analysis still runs).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-gray-600">{(wizardData.showAgentSentiment ?? true) ? 'Shown' : 'Hidden'}</span>
            <InputSwitch
              checked={wizardData.showAgentSentiment ?? true}
              onChange={(e) => set({ showAgentSentiment: e.value ?? true })}
              disabled={disabled}
              aria-label="Show agent sentiment"
            />
          </div>
        </div>

        <div className={`bg-white rounded-sm border border-gray-100 overflow-hidden transition-opacity ${enabled ? '' : 'opacity-60'}`}>
          <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">AWS Transcribe Credentials</h3>
            {missing && (
              <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
                <i className="pi pi-exclamation-triangle text-[10px]" /> Required to transcribe calls
              </span>
            )}
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">Access Key ID {enabled && <span className="text-red-500">*</span>}</label>
              <InputText
                value={wizardData.awsTranscribeAccessKey ?? ''}
                onChange={(e) => set({ awsTranscribeAccessKey: e.target.value })}
                className="w-full font-mono"
                placeholder="AKIA…"
                autoComplete="off"
                disabled={disabled}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">Secret Access Key {enabled && !secretStored && <span className="text-red-500">*</span>}</label>
              <InputText
                type="password"
                value={wizardData.awsTranscribeSecret ?? ''}
                onChange={(e) => set({ awsTranscribeSecret: e.target.value })}
                className="w-full font-mono"
                placeholder={secretStored ? '•••••••• (stored — leave blank to keep)' : 'Secret access key'}
                autoComplete="new-password"
                disabled={disabled}
              />
              <small className="text-gray-400">Stored encrypted. It is never shown again after saving.</small>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">AWS Region {enabled && <span className="text-red-500">*</span>}</label>
              <Dropdown
                value={wizardData.awsTranscribeRegion ?? ''}
                options={[{ label: 'Select AWS Region', value: '' }, ...AWS_REGIONS.map((r) => ({ label: r, value: r }))]}
                onChange={(e) => set({ awsTranscribeRegion: e.value as string })}
                className="w-full"
                disabled={disabled}
                filter
                appendTo={document.body}
                pt={{ panel: { style: { zIndex: 100000 } }, list: { className: 'max-h-60 overflow-auto' } } as any}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">S3 Bucket {enabled && <span className="text-red-500">*</span>}</label>
              <InputText
                value={wizardData.awsTranscribeS3Bucket ?? ''}
                onChange={(e) => set({ awsTranscribeS3Bucket: e.target.value })}
                className="w-full font-mono"
                placeholder="my-transcribe-bucket"
                disabled={disabled}
              />
              <small className="text-gray-400">Must be in the same region. Audio is staged here and deleted once transcribed.</small>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">Language</label>
              <Dropdown
                value={wizardData.awsTranscribeLanguageCode ?? 'en-US'}
                options={TRANSCRIBE_LANGUAGES}
                onChange={(e) => set({ awsTranscribeLanguageCode: e.value as string })}
                className="w-full"
                disabled={disabled}
                appendTo={document.body}
                pt={{ panel: { style: { zIndex: 100000 } } } as any}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">IAM ARN</label>
              <InputText
                value={wizardData.awsTranscribeIamArn ?? ''}
                onChange={(e) => set({ awsTranscribeIamArn: e.target.value })}
                className={`w-full font-mono ${arnInvalid ? 'p-invalid' : ''}`}
                placeholder="arn:aws:iam::123456789012:user/transcribe"
                disabled={disabled}
              />
              {arnInvalid ? (
                <small className="text-red-500">Expected arn:aws:iam::&lt;12-digit account&gt;:user/… or :role/…</small>
              ) : (
                <small className="text-gray-400">
                  {arnIsRole
                    ? 'Role ARN — passed to Call Analytics as the data-access role.'
                    : 'The IAM user (or role) these credentials belong to. A role ARN is also used as the Call Analytics data-access role.'}
                </small>
              )}
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50/60 border-t border-gray-50 text-xs text-gray-500 leading-relaxed">
            The IAM user needs <span className="font-mono">transcribe:StartTranscriptionJob</span>,{' '}
            <span className="font-mono">transcribe:GetTranscriptionJob</span>,{' '}
            <span className="font-mono">transcribe:StartCallAnalyticsJob</span>,{' '}
            <span className="font-mono">transcribe:GetCallAnalyticsJob</span> and{' '}
            <span className="font-mono">s3:PutObject / GetObject / DeleteObject</span> on the bucket.
          </div>
        </div>

        {renderLiveAssistEngine()}
      </div>
    )
  }

  const renderLiveAssistEngine = (): JSX.Element => {
    const disabled = mode === 'view'
    const engine = wizardData.liveAssistEngine ?? 'aws'
    const set = (patch: Partial<ProvisionWizardData>): void =>
      setWizardData((prev) => ({ ...prev, ...patch }))
    const url = wizardData.liveAssistWhisperUrl?.trim() ?? ''
    const urlInvalid = url !== '' && !WHISPER_URL_RE.test(url)
    const keyStored = mode !== 'create' && Boolean(tenant?.liveAssistWhisperKeyConfigured)
    const model = wizardData.liveAssistModel || DEFAULT_SELF_HOSTED_MODEL
    const modelOptions = SELF_HOSTED_MODELS.some((m) => m.value === model)
      ? SELF_HOSTED_MODELS
      : [...SELF_HOSTED_MODELS, { label: model, value: model }]
    const options = [
      { value: 'aws' as const, title: 'AWS', icon: 'pi-cloud', text: 'AWS Transcribe streaming + the tenant’s cloud AI routing. Uses the Transcribe credentials above.' },
      { value: 'self_hosted' as const, title: 'Self-hosted', icon: 'pi-server', text: 'faster-whisper speech-to-text + Gemma via Ollama. Audio and transcripts stay on your servers.' },
    ]
    return (
      <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Live Call Assist Engine</h3>
          {engine === 'self_hosted' && !url && (
            <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
              <i className="pi pi-exclamation-triangle text-[10px]" /> Whisper server URL required
            </span>
          )}
        </div>
        <div className="p-6 flex flex-col gap-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={disabled}
                onClick={() => set({
                  liveAssistEngine: o.value,
                  ...(o.value === 'self_hosted' && !url ? { liveAssistWhisperUrl: DEFAULT_WHISPER_URL } : {}),
                })}
                className={`text-left rounded-sm border px-4 py-3 transition-colors ${engine === o.value ? 'border-[#3454cf] bg-[#3454cf]/5' : 'border-gray-200 hover:bg-gray-50'} ${disabled ? 'cursor-default' : ''}`}
              >
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <i className={`pi ${o.icon} text-[#3454cf] text-xs`} /> {o.title}
                </p>
                <p className="text-xs text-gray-500 mt-1">{o.text}</p>
              </button>
            ))}
          </div>

          {engine === 'self_hosted' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Whisper Server URL <span className="text-red-500">*</span></label>
                <InputText
                  value={wizardData.liveAssistWhisperUrl ?? ''}
                  onChange={(e) => set({ liveAssistWhisperUrl: e.target.value })}
                  className={`w-full font-mono ${urlInvalid ? 'p-invalid' : ''}`}
                  placeholder="http://host:8178"
                  disabled={disabled}
                />
                <small className={urlInvalid ? 'text-red-500' : 'text-gray-400'}>
                  {urlInvalid ? 'Expected http(s):// or ws(s):// followed by host[:port]' : 'Must be reachable from the ws-gateway container (services/stt-whisper).'}
                </small>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Whisper API Key</label>
                <InputText
                  type="password"
                  value={wizardData.liveAssistWhisperKey ?? ''}
                  onChange={(e) => set({ liveAssistWhisperKey: e.target.value })}
                  className="w-full font-mono"
                  placeholder={keyStored ? '•••••••• (stored — leave blank to keep)' : 'Optional — the server’s STT_API_KEY'}
                  autoComplete="new-password"
                  disabled={disabled}
                />
                <small className="text-gray-400">Stored encrypted. Leave empty if the server has no key.</small>
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Live Assist AI Model</label>
                <Dropdown
                  value={model}
                  options={modelOptions}
                  onChange={(e) => set({ liveAssistModel: e.value as string })}
                  className="w-full"
                  disabled={disabled}
                  editable
                  appendTo={document.body}
                  pt={{ panel: { style: { zIndex: 100000 } } } as any}
                />
                <small className="text-gray-400">
                  provider:model. Runs on the tenant’s Ollama provider with no cloud fallback. Pull the model on the Ollama host first
                  (<span className="font-mono">ollama pull {model.split(':').slice(1).join(':')}</span>).
                </small>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400">
            This is the default for the tenant. Its admin can change it later in AI Center → Live Call Assist.
          </p>
        </div>
      </div>
    )
  }

  const renderDefaultsTab = (): JSX.Element => {
    const seedPreviewData = getSeedPreviewData(wizardData.companyType ?? 'general')

    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Company Defaults</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">Company Type</label>
              <Dropdown
                value={wizardData.companyType}
                options={[
                  { label: 'General', value: 'general' },
                  { label: 'BPO', value: 'bpo' },
                  { label: 'Healthcare', value: 'healthcare' },
                  { label: 'E-commerce', value: 'ecommerce' },
                  { label: 'Financial Services', value: 'financial_services' },
                  { label: 'Logistics', value: 'logistics' },
                ]}
                onChange={(e) => setWizardData((prev) => ({ ...prev, companyType: e.value as string }))}
                placeholder="Select Company Type"
                className="w-full"
                disabled={mode === 'view'}
                filter
                appendTo={document.body}
                scrollHeight="200px"
                pt={{ panel: { style: { zIndex: 100000 } } as any }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">Seed Default Data Automatically</label>
              <div className="flex items-center gap-3">
                <InputSwitch
                  checked={wizardData.seedDefaultData ?? false}
                  onChange={(e) => setWizardData((prev) => ({ ...prev, seedDefaultData: e.value ?? false }))}
                  disabled={mode === 'view'}
                />
                <span className="text-sm text-gray-600">{wizardData.seedDefaultData ? 'Yes' : 'No'}</span>
              </div>
              <small className="text-gray-500">Master data will be seeded during provisioning if enabled.</small>
            </div>
          </div>
        </div>

        {wizardData.seedDefaultData && (
          <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Seed Preview</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                The following master data will be seeded for a <strong>{wizardData.companyType}</strong> tenant:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(seedPreviewData).map(([group, values]) => (
                  <div key={group} className="bg-gray-50 rounded-sm p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <i className="pi pi-check-circle text-emerald-500" />
                      {group}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {values.map((value) => (
                        <span key={value.label} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-white border border-gray-100 text-xs text-gray-700">
                          {value.color && (
                            <span
                              className="h-3 w-3 rounded-full border border-gray-200"
                              style={{ backgroundColor: value.color }}
                            />
                          )}
                          {value.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ---- Main active step renderer ------------------------------------------

  const renderStepContent = (): JSX.Element => {
    switch (activeStep) {
      case 0:
        return renderCombinedBasicInfoTab()
      case 1:
        return renderTransferOptionsTab()
      case 2:
        return renderCombinedClientCompanyTab()
      case 3:
        return (
          <ProvisionStepRecording
            value={wizardData.recording ?? DEFAULT_TENANT_RECORDING_CONFIG}
            onChange={(recording) => setWizardData((prev) => ({ ...prev, recording }))}
            readOnly={mode === 'view'}
          />
        )
      case 4:
        return renderAwsS3Tab()
      case 5:
        return renderTranscriptionTab()
      case 6:
        return renderDefaultsTab()
      case REVIEW_STEP:
        return (
          <ProvisionStepReview
            ref={reviewRef}
            data={wizardData}
            timezones={timezones}
            onComplete={handleComplete}
            mode={mode}
            tenant={tenant}
            callAnalytics={callAnalytics}
            googleSignIn={googleSignIn}
            googleClientId={googleClientId}
            googleClientSecret={googleClientSecret}
            microsoftSignIn={microsoftSignIn}
            microsoftClientId={microsoftClientId}
            microsoftClientSecret={microsoftClientSecret}
            continueRecording={continueRecording}
            transferFeatures={transferFeatures as unknown as Record<string, boolean>}
          />
        )
      default:
        return <></>
    }
  }

  // ---- Bottom navigation for all steps --------------------------

  const renderBottomNav = (): JSX.Element | null => {
    if (mode === 'view') {
      return (
        <div className="flex items-center justify-end border-t border-gray-100 px-6 py-4 bg-white">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
          >
            <i className="pi pi-check text-xs" />
            Done
          </button>
        </div>
      )
    }

    // Step 0: Next only
    if (activeStep === 0) {
      const slugValid = tenantSlugSchema.safeParse(wizardData.slug).success
      const nameValid = wizardData.name.trim().length >= 1 && wizardData.name.trim().length <= 100
      const isValidUrl = (url: string): boolean => {
        if (!url) return true
        try {
          const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
          return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
        } catch { return false }
      }
      const domainUrlValid = !wizardData.domainUrl || isValidUrl(wizardData.domainUrl)
      const googleFeatureFlags = tenant?.featureFlags?.googleSignIn as { clientSecretEncrypted?: { c: string; i: string; t: string } } | undefined
      const hasExistingGoogleSecret = mode === 'edit' && Boolean(googleFeatureFlags?.clientSecretEncrypted)
      const googleConfigValid = !googleSignIn || (
        googleClientId.trim().length > 0 &&
        (hasExistingGoogleSecret || googleClientSecret.trim().length > 0)
      )
      const microsoftFeatureFlags = tenant?.featureFlags?.microsoftSignIn as { clientSecretEncrypted?: { c: string; i: string; t: string } } | undefined
      const hasExistingMicrosoftSecret = mode === 'edit' && Boolean(microsoftFeatureFlags?.clientSecretEncrypted)
      const microsoftConfigValid = !microsoftSignIn || (
        microsoftClientId.trim().length > 0 &&
        (hasExistingMicrosoftSecret || microsoftClientSecret.trim().length > 0)
      )
      const canAdvance = slugValid && nameValid && domainUrlValid && wizardData.timeZone && wizardData.userLimitation && googleConfigValid && microsoftConfigValid

      return (
        <div className="flex items-center justify-end border-t border-gray-100 px-6 py-4 bg-white">
          <button
            disabled={!canAdvance}
            onClick={() => setActiveStep(1)}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
          >
            Next
            <i className="pi pi-arrow-right text-xs" />
          </button>
        </div>
      )
    }

    // Review step: Previous + Provision
    if (activeStep === REVIEW_STEP) {
      const inFlight = reviewRef.current?.inFlight ?? false
      const phase = reviewRef.current?.phase ?? 'idle'
      return (
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-white">
          <button
            onClick={() => setActiveStep(REVIEW_STEP - 1)}
            disabled={inFlight}
            className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
          >
            <i className="pi pi-arrow-left text-xs" />
            Previous
          </button>
          <button
            onClick={() => reviewRef.current?.handleProvision()}
            disabled={inFlight}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
          >
            {inFlight ? <i className="pi pi-spin pi-spinner text-xs" /> : <i className="pi pi-check text-xs" />}
            {phase === 'error' ? 'Retry' : mode === 'edit' ? 'Update Tenant' : 'Provision Instance'}
          </button>
        </div>
      )
    }

    // Steps 1-6: Previous + Next
    const nextBlocked =
      activeStep === 3 &&
      Object.keys(validateRecording(wizardData.recording ?? DEFAULT_TENANT_RECORDING_CONFIG)).length > 0
    return (
      <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-white">
        <button
          onClick={() => setActiveStep((s) => s - 1)}
          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all"
        >
          <i className="pi pi-arrow-left text-xs" />
          Previous
        </button>
        <button
          disabled={nextBlocked}
          onClick={() => setActiveStep((s) => s + 1)}
          className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
        >
          Next
          <i className="pi pi-arrow-right text-xs" />
        </button>
      </div>
    )
  }

  // ---- Don't render anything when closed ----------------------------------

  if (!open) return <></>

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="New Tenant"
        className="fixed right-0 top-[0px] z-[9999] flex h-[100vh] w-full max-w-4xl flex-col bg-white shadow-2xl animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-building text-white text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {mode === 'create' ? 'New Tenant' : mode === 'edit' ? 'Edit Tenant' : 'View Tenant'}
              </h2>
              <p className="text-xs text-gray-400">Step {activeStep + 1} of {WIZARD_STEPS.length}</p>
            </div>
          </div>
          <button
            type="button"
            className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="border-b border-gray-100 px-6 py-3 shrink-0 overflow-x-auto bg-white">
          <Steps
            model={WIZARD_STEPS as unknown as { label: string }[]}
            activeIndex={activeStep}
            onSelect={(e) => setActiveStep(e.index)}
            readOnly={false}
            pt={{
              root: { className: 'w-full' },
              menu: { className: 'flex flex-row gap-2' },
              item: { className: 'flex-1' },
              label: { className: 'text-sm font-medium text-gray-500' },
              active: { className: 'text-[#3454cf] font-semibold' }
            } as any}
          />
        </div>

        {/* Scrollable step body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#fafafa]">
          {renderStepContent()}
        </div>

        {/* Bottom nav for extended tabs */}
        {renderBottomNav()}
      </aside>
    </>,
    document.body
  )
}

export default ProvisionWizardDrawer
